import React from 'react';
import debounce from 'lodash/debounce';
import memoizeOne from 'memoize-one';
import type { FieldProps } from '@atlaskit/form';
import { Inline } from '@atlaskit/primitives';
import { type AsyncSelectProps, AsyncSelect } from '@atlaskit/select';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useServiceDeskContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/index.tsx';
import messages from './messages';
import type { RequestTypesResponse, RequestTypeOption } from './types';
import { toRequestTypeOption } from './utils';

const DEBOUNCE_DELAY = 100;

const buildRequestTypeSearchUrl = (serviceDeskId: number, query: string) => {
	const queryString = new URLSearchParams([
		['limit', '100'],
		['includeHiddenRequestTypesInSearch', 'true'],
		['restrictionStatus', 'open'], // Only allow open (non-restricted) request types in the virtual service agent to be selected.
		['searchQuery', query],
	]).toString();

	return `/rest/servicedeskapi/servicedesk/${serviceDeskId}/requesttype?${queryString}`;
};

const createDefaultLoadOptions = memoizeOne((serviceDeskId: number) =>
	debounce((inputValue: string, callback: (requestTypes: readonly RequestTypeOption[]) => void) => {
		performGetRequest(buildRequestTypeSearchUrl(serviceDeskId, inputValue))
			.then((response: RequestTypesResponse) => {
				callback(response.values.map(toRequestTypeOption));
			})
			.catch((error) => {
				fireErrorAnalytics({
					meta: {
						id: 'fetchRequestTypesForRequestTypeSelect',
						teamName: 'jsd-flux-capacitors',
						packageName: 'jiraServicedeskSettingsVirtualAgentCommon',
					},
					error,
				});
			});
	}, DEBOUNCE_DELAY),
);

// Makes only the second argument's props required, and everything else is optional
type MakeOnlyRequiredProps<T, RequiredProps extends keyof T> = Required<Pick<T, RequiredProps>> &
	Partial<Omit<T, RequiredProps>>;

export const RequestTypeSelect = (
	props: Pick<AsyncSelectProps<RequestTypeOption>, 'isLoading' | 'inputId' | 'onChange'> &
		// All props from `FieldProps` except 'onChange', but only `value` is required
		Omit<MakeOnlyRequiredProps<FieldProps<RequestTypeOption | null>, 'value'>, 'onChange'>,
) => {
	const { formatMessage } = useIntl();
	const [{ data: serviceDeskContext }] = useServiceDeskContext();
	const { serviceDeskId } = serviceDeskContext || {};

	if (!serviceDeskId) {
		return null;
	}

	return (
		<AsyncSelect<RequestTypeOption>
			{...props}
			testId="servicedesk-settings-virtual-agent-common.ui.request-type-select.request-type-select"
			cacheOptions
			defaultOptions
			loadOptions={createDefaultLoadOptions(serviceDeskId)}
			placeholder={formatMessage(messages.selectPlaceholder)}
			noOptionsMessage={({ inputValue }) => formatMessage(messages.noMatches, { inputValue })}
			formatOptionLabel={(requestType) => (
				<Inline
					space="space.100"
					alignBlock="center"
					testId={`servicedesk-settings-virtual-agent-common.ui.request-type-select.option-${requestType.name}`}
				>
					{requestType.iconUrl != null && (
						<img src={requestType.iconUrl} width="24" height="24" alt="" />
					)}
					<span>{requestType.name}</span>
				</Inline>
			)}
			getOptionValue={(requestType) => requestType.id}
		/>
	);
};
